// Persistent profile: Fracture Shard wallet, Array Workshop ranks, records. Versioned + migrated + backed up.
// Storage is injected so the logic is unit-testable without a browser.
import { WORKSHOP, WorkshopId, WorkshopRanks, workshopCost } from '../core/content';
import { nodeOf, reqsMet } from '../core/powerups'; // POWER UPS prerequisites (2026-11-02)
import { Codex, emptyCodex, migrateCodex } from './codex';
import { A11yPrefs, LegacyA11y, defaultA11y, migrateA11y } from './a11yPrefs';
import { emptyMaps, MapsSave, migrateMaps } from './mapSave';
import { SettingsPrefs, defaultSettings, migrateSettings } from './settingsPrefs';
import { MenuPrefs, defaultMenuPrefs, migrateMenuPrefs } from './menuPrefs';
import { EndlessRecords, migrateEndless } from './endlessSave';
import { FeatsSave, emptyFeats, migrateFeats } from './feats';
import { TipsSave, migrateTips } from './tipsSave';
import { SURGE_TIERS } from '../core/skillStats'; // SURGE FORMS: the best-form record is clamped to the forms that exist

/** v2 (2026-11-02): + codex (Encyclopedia discoveries and lifetime counters, src/app/codex.ts). v1 saves gain an empty one.
 *  v3 (2026-11-02, map system): + maps (selected map, time of day per map, best time per map, unlocked maps; src/app/mapSave.ts).
 *  v4 (2026-11-02): + a11y (comfort & accessibility settings, src/app/a11yPrefs.ts). Older saves seed it from the legacy
 *  localStorage keys (flashes / shake / uisize) when main.ts passes them, else the defaults.
 *  v5 (2026-11-02, settings expansion): + settings (master / SFX volume, enemy health bars, brightness, FPS counter,
 *  controller vibration, touch layout; src/app/settingsPrefs.ts). Older saves get the defaults.
 *  v6 (2026-11-02, menu look): + menu (menu font, text outline, layout density, panel opacity; src/app/menuPrefs.ts).
 *  Older saves get the defaults (Classic font). */
export const SAVE_VERSION = 6;
export const SAVE_KEY = 'arena.save';
export const BACKUP_KEY = 'arena.save.bak';

/** per-difficulty tally (Run Setup Difficulty row: relaxed / normal / hard / brutal / custom) */
export interface DifficultyRecord { runs: number; wins: number; bestTime: number }
export interface Records { runs: number; wins: number; bestTime: number; bestKills: number; bestLevel: number;
  /** runs / victories / best time by difficulty played; absent until a run is recorded with one (no schema bump) */
  byDifficulty?: Record<string, DifficultyRecord>;
  /** SURGE FORMS (2026-11-03): the highest Surge form ever reached (1..8); absent until a run transforms (no schema bump) */
  bestForm?: number }
const DIFF_KEYS = ['relaxed', 'normal', 'hard', 'brutal', 'custom'];
export interface Profile {
  v: number;
  shards: number;
  /** Lifetime shards earned (never decreases; used for refund safety checks). */
  earned: number;
  /** Shards currently invested in the Workshop (refundable). */
  invested: number;
  workshop: WorkshopRanks;
  records: Records;
  ledger: { t: number; kind: 'earn' | 'buy' | 'refund'; amount: number; note: string }[];
  /** Encyclopedia: what has been discovered, with lifetime counters (v2) */
  codex: Codex;
  /** Map select: selected map, time of day per map, best survival time per map, unlocked maps (v3) */
  maps: MapsSave;
  /** comfort & accessibility settings (v4) */
  a11y: A11yPrefs;
  /** audio mix, display, vibration and touch-layout settings (v5) */
  settings: SettingsPrefs;
  /** menu font, text outline, layout density, panel opacity (v6) */
  menu: MenuPrefs;
  /** ENDLESS GOALS (2026-11-02): best endless time / tier, overall and per map (src/app/endlessSave.ts). OPTIONAL, no
   *  version bump: absent until a run reaches endless; any saved shape loads. */
  endless?: EndlessRecords;
  /** ACHIEVEMENTS / QUESTS / CHALLENGES (2026-11-02, src/app/feats.ts). OPTIONAL, no version bump: any save without it (or
   *  with a partial / junk one) loads with defaults via migrateFeats; read it through featsOf(). */
  feats?: FeatsSave;
  /** TUTORIAL (2026-11-02, OPTIONAL — no version bump): first-time hints seen, Tips on/off, Basics lesson offered / done
   *  (src/app/tipsSave.ts, tutorial.ts). Missing = nothing seen yet, tips on. */
  tips?: TipsSave;
}

export interface KV { getItem(k: string): string | null; setItem(k: string, v: string): void }

export function freshProfile(): Profile {
  return { v: SAVE_VERSION, shards: 0, earned: 0, invested: 0, workshop: {}, records: { runs: 0, wins: 0, bestTime: 0, bestKills: 0, bestLevel: 0 }, ledger: [], codex: emptyCodex(), maps: emptyMaps(), a11y: defaultA11y(), settings: defaultSettings(), menu: defaultMenuPrefs(), feats: emptyFeats() };
}
/** the profile's achievements / quests / challenges block (created on first use) */
export const featsOf = (p: Profile): FeatsSave => p.feats ?? (p.feats = emptyFeats());

/** Upgrade any older/partial shape to the current schema. Never drops shards or ranks.
 *  `legacy`: pre-v4 localStorage a11y keys, used only when the save has no a11y block yet. */
export function migrate(raw: unknown, legacy?: LegacyA11y): Profile {
  const p = freshProfile();
  if (!raw || typeof raw !== 'object') { p.a11y = migrateA11y(null, legacy); return p; }
  const r = raw as Partial<Profile> & { v?: number };
  const num = (x: unknown, d = 0) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : d);
  p.shards = Math.floor(num(r.shards));
  p.earned = Math.max(Math.floor(num(r.earned)), p.shards);
  if (r.workshop && typeof r.workshop === 'object') {
    for (const t of WORKSHOP) {
      const rank = Math.min(t.max, Math.floor(num((r.workshop as Record<string, unknown>)[t.id])));
      if (rank > 0) p.workshop[t.id] = rank;
    }
  }
  // keep what the player actually paid (prices can change between versions); older saves without it use the price table
  p.invested = typeof r.invested === 'number' && Number.isFinite(r.invested) && r.invested >= 0 ? Math.floor(r.invested) : investedFor(p.workshop);
  if (r.records && typeof r.records === 'object') {
    const rr = r.records as Partial<Records>;
    p.records = { runs: num(rr.runs), wins: num(rr.wins), bestTime: num(rr.bestTime), bestKills: num(rr.bestKills), bestLevel: num(rr.bestLevel) };
    if (rr.byDifficulty && typeof rr.byDifficulty === 'object') {
      const by: Record<string, DifficultyRecord> = {};
      for (const k of DIFF_KEYS) { const d = (rr.byDifficulty as Record<string, Partial<DifficultyRecord> | undefined>)[k]; if (d && typeof d === 'object') by[k] = { runs: Math.floor(num(d.runs)), wins: Math.floor(num(d.wins)), bestTime: num(d.bestTime) }; }
      if (Object.keys(by).length) p.records.byDifficulty = by;
    }
    { const bf = Math.floor(num(rr.bestForm)); if (bf > 0) p.records.bestForm = Math.min(SURGE_TIERS, bf); // SURGE FORMS (optional, any version)
    }
  }
  if (Array.isArray(r.ledger)) p.ledger = r.ledger.slice(-50);
  p.codex = migrateCodex(r.codex); // v1 → v2: no codex yet, starts empty
  p.maps = migrateMaps(r.maps, p.records.bestTime); // v2 → v3: no maps yet; the old best time counts for map #1
  p.a11y = migrateA11y(r.a11y, legacy); // v3 → v4: seeded from the legacy keys, else defaults
  p.settings = migrateSettings(r.settings); // v4 → v5: defaults
  p.menu = migrateMenuPrefs(r.menu); // v5 → v6: defaults
  { const en = migrateEndless(r.endless); if (en) p.endless = en; } // optional field, any version
  p.feats = migrateFeats(r.feats); // optional block (any version): missing / junk → defaults
  const tips = migrateTips(r.tips); if (tips) p.tips = tips; // optional block (tutorial): kept when present, never required
  p.v = SAVE_VERSION;
  return p;
}

export function investedFor(ranks: WorkshopRanks): number {
  let sum = 0;
  for (const t of WORKSHOP) for (let n = 1; n <= (ranks[t.id] ?? 0); n++) sum += workshopCost(t, n);
  return sum;
}

export function load(kv: KV | null, legacy?: LegacyA11y): Profile {
  if (!kv) return freshProfile();
  try {
    const s = kv.getItem(SAVE_KEY);
    if (!s) return migrate(null, legacy); // no save yet: still carry over the old a11y keys
    const raw = JSON.parse(s);
    const p = migrate(raw, legacy);
    // keep a copy of the pre-migration save whenever the schema changes
    if ((raw as { v?: number }).v !== SAVE_VERSION) kv.setItem(BACKUP_KEY, s);
    return p;
  } catch {
    try { const b = kv.getItem(BACKUP_KEY); if (b) return migrate(JSON.parse(b), legacy); } catch { /* fall through */ }
    return freshProfile();
  }
}

export function store(kv: KV | null, p: Profile): void {
  if (!kv) return;
  try { kv.setItem(SAVE_KEY, JSON.stringify(p)); } catch { /* storage full/blocked: keep in memory */ }
}

function log(p: Profile, kind: 'earn' | 'buy' | 'refund', amount: number, note: string) {
  p.ledger.push({ t: Date.now(), kind, amount, note });
  if (p.ledger.length > 50) p.ledger.splice(0, p.ledger.length - 50);
}

export function nextCost(p: Profile, id: WorkshopId): number | null {
  const t = WORKSHOP.find((x) => x.id === id)!;
  const r = p.workshop[id] ?? 0;
  return r >= t.max ? null : workshopCost(t, r + 1);
}

/** why a node cannot be bought right now (null = it can) */
export function buyBlock(p: Profile, id: WorkshopId): 'max' | 'locked' | 'poor' | null {
  const cost = nextCost(p, id);
  if (cost === null) return 'max';
  if (!reqsMet(p.workshop, nodeOf(id))) return 'locked'; // POWER UPS: prerequisites gate buying (owned ranks are never removed)
  return p.shards < cost ? 'poor' : null;
}

export function buy(p: Profile, id: WorkshopId): boolean {
  const cost = nextCost(p, id);
  if (cost === null || buyBlock(p, id) !== null) return false;
  p.shards -= cost; p.invested += cost;
  p.workshop[id] = (p.workshop[id] ?? 0) + 1;
  log(p, 'buy', cost, `${id} → ${p.workshop[id]}`);
  return true;
}

/** Full respec: every shard actually paid comes back (not re-priced at today's costs). */
export function refundAll(p: Profile): number {
  const back = p.invested;
  p.shards += back; p.invested = 0; p.workshop = {};
  if (back > 0) log(p, 'refund', back, 'respec');
  return back;
}

export interface RunResult { shards: number; time: number; kills: number; level: number; victory: boolean;
  /** run modifiers that were on (ids, src/core/mods.ts); noted in the ledger entry. No schema change. */
  mods?: string[];
  /** the Run Setup difficulty played (relaxed / normal / hard / brutal / custom); noted in the ledger and the records */
  difficulty?: string;
  /** SURGE FORMS: the highest form reached this run (0 = none) */
  form?: number }
/** shards paid outside a run's payout (achievements, quests, challenges): wallet + lifetime + ledger */
export function grantShards(p: Profile, amount: number, note: string): void {
  const n = Math.max(0, Math.floor(amount)); if (!n) return;
  p.shards += n; p.earned += n; log(p, 'earn', n, note);
}

export function recordRun(p: Profile, r: RunResult): { newBest: string[] } {
  const amt = Math.max(0, Math.floor(r.shards));
  p.shards += amt; p.earned += amt;
  if (amt > 0) log(p, 'earn', amt, (r.victory ? 'victory' : 'run') + (r.difficulty ? ` [${r.difficulty}]` : '') + (r.mods?.length ? ` +${r.mods.join(',')}` : ''));
  const rec = p.records, newBest: string[] = [];
  rec.runs++; if (r.victory) rec.wins++;
  if (r.time > rec.bestTime) { rec.bestTime = r.time; newBest.push('time'); }
  if (r.kills > rec.bestKills) { rec.bestKills = r.kills; newBest.push('kills'); }
  if (r.level > rec.bestLevel) { rec.bestLevel = r.level; newBest.push('level'); }
  if (r.form && r.form > (rec.bestForm ?? 0)) { rec.bestForm = Math.floor(r.form); newBest.push('form'); }
  if (r.difficulty && DIFF_KEYS.includes(r.difficulty)) {
    const by = (rec.byDifficulty ??= {}), d = (by[r.difficulty] ??= { runs: 0, wins: 0, bestTime: 0 });
    d.runs++; if (r.victory) d.wins++; if (r.time > d.bestTime) d.bestTime = r.time;
  }
  return { newBest };
}

export function browserKV(): KV | null {
  try { const k = '__t'; localStorage.setItem(k, '1'); localStorage.removeItem(k); return localStorage; } catch { return null; }
}
